'use client'

import { Button, Cell, Dialog, List, Radio, Spacer, Text } from '@vinted/web-ui'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import {
  InputDropdown,
  InputDropdownRenderProps,
} from '@marketplace-web/common-components/input-dropdown-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'

import { BusinessRegistrationModel } from '@marketplace-web/business-accounts/business-accounts-data'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { NewBusinessAddressField, NewBusinessDetailsField } from '../../../../../../constants'

export type InputDropdownOption = {
  value: string
  label: string
}

type Props = {
  title: string
  defaultValue?: string
  options: Array<InputDropdownOption>
  input: NewBusinessAddressField | NewBusinessDetailsField
  translationPrefix: string
  required?: boolean
  disabled?: boolean
  withWarningModal?: boolean
  warningModalTitle?: string
  warningModalBody?: string
  warningModalContinueText?: string
  warningModalCancelText?: string
  onChange?: (value: string) => void
  onCancel?: () => void
}

const InputDropdownController = ({
  title,
  defaultValue,
  options,
  input,
  translationPrefix,
  required,
  disabled,
  withWarningModal,
  warningModalTitle,
  warningModalBody,
  warningModalContinueText,
  warningModalCancelText,
  onChange,
  onCancel,
}: Props) => {
  const {
    setValue,
    control,
    watch,
    formState: { errors },
  } = useFormContext<Partial<BusinessRegistrationModel>>()
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [possibleUpdatedValue, setPossibleUpdatedValue] = useState<string | undefined>()

  const breakpoints = useBreakpoint()
  const inputDropdownTranslate = useTranslate()
  const validationMessage = useFormValidationMessage(errors, translationPrefix)

  const inputValue = watch(input)
  const selectedOption = options.find(option => option.value === inputValue)

  const changeInputValue = useCallback(
    (value: string) => {
      setValue(input, value, { shouldValidate: true, shouldDirty: true })
    },
    [setValue, input],
  )

  useEffect(() => {
    if (!defaultValue) return
    changeInputValue(defaultValue)
  }, [changeInputValue, defaultValue])

  const handleInputDropdownValueChange =
    (option: InputDropdownOption, inputRenderProps: InputDropdownRenderProps) => () => {
      inputRenderProps.setIsSaveEnabled(true)

      const shouldShowWarningModal = withWarningModal && !!inputValue && inputValue !== option.value

      if (shouldShowWarningModal) {
        setPossibleUpdatedValue(option.value)
        setShowWarningModal(true)
      } else {
        changeInputValue(option.value)
      }

      if (breakpoints.phones) return

      inputRenderProps.closeDropdown()
    }

  const handleContinueValueChange = () => {
    if (!possibleUpdatedValue) return
    onChange?.(possibleUpdatedValue)
    changeInputValue(possibleUpdatedValue)
    setShowWarningModal(false)
    setPossibleUpdatedValue(undefined)
  }

  const handleCancelValueChange = () => {
    setShowWarningModal(false)
    setPossibleUpdatedValue(undefined)
    onCancel?.()
  }

  const renderDropdownOption = (
    option: InputDropdownOption,
    inputRenderProps: InputDropdownRenderProps,
  ) => {
    const isOptionSelected = option.value === selectedOption?.value

    return (
      <Cell
        key={option.value}
        title={option.label}
        onClick={handleInputDropdownValueChange(option, inputRenderProps)}
        id={`id-${option.value}`}
        suffix={
          <Radio
            name={`radio-${option.label}`}
            checked={isOptionSelected}
            onChange={() => undefined}
            aria={{ 'aria-labelledby': `id-${option.value}` }}
          />
        }
      />
    )
  }

  const renderOptions = (inputRenderProps: InputDropdownRenderProps) => (
    <List>{options.map(option => renderDropdownOption(option, inputRenderProps))}</List>
  )

  return (
    <>
      <Controller
        name={input}
        render={({ field: { name } }) => (
          <InputDropdown
            isPhone={!!breakpoints.phones}
            textSave={inputDropdownTranslate('actions.save')}
            name={name}
            disabled={disabled}
            title={title}
            placeholder={title}
            render={renderOptions}
            value={selectedOption?.label || defaultValue}
            validation={renderValidation(validationMessage(input))}
          />
        )}
        rules={{
          required,
        }}
        control={control}
      />
      {withWarningModal && (
        <Dialog show={showWarningModal}>
          <Cell>
            <Text
              as="h2"
              text={warningModalTitle}
              type={Text.Type.Heading}
              width={Text.Width.Parent}
              alignment={Text.Alignment.Center}
            />
            <Spacer size={Spacer.Size.Large} />
            <Text
              as="p"
              text={warningModalBody}
              type={Text.Type.Body}
              width={Text.Width.Parent}
              alignment={Text.Alignment.Left}
            />
          </Cell>
          <Cell>
            <Button
              text={warningModalContinueText}
              styling={Button.Styling.Filled}
              onClick={handleContinueValueChange}
            />
            <Spacer size={Spacer.Size.Medium} />
            <Button
              text={warningModalCancelText}
              styling={Button.Styling.Flat}
              onClick={handleCancelValueChange}
            />
          </Cell>
        </Dialog>
      )}
    </>
  )
}

export default InputDropdownController
